/**
 * App controller: the main-thread orchestrator the UI binds to. It owns the stores (scene with
 * undo, settings, preset library, keymap, MIDI, outputs, Auto show) and the Engine, and exposes
 * reactive state for Svelte. Every user-facing operation is a method here, so keyboard, MIDI,
 * buttons and Auto show all go through the same paths.
 */
import { tick } from 'svelte';
import { builtInPresets } from '../content/presets';
import {
  ACTIONS,
  chordOf,
  defaultKeymap,
  isReserved,
  keyHint,
  sanitizeKeymap,
  upgradeKeymap,
  type Binding,
  type Keymap,
} from '../core/actions';
import { choosePreset } from '../core/autopilot';
import { Director, type Move } from '../core/director';
import {
  calmDown,
  changeBackground,
  changeColours,
  changeEffects,
  changeTop,
  wholeNewLook,
  type MoveContext,
} from '../core/moves';
import { MidiController } from '../core/MidiController';
import type { MidiTarget } from '../core/midi';
import { OutputManager, type OutputMode } from '../core/outputs';
import { BANKS, PresetLibrary, SLOTS, type Preset } from '../core/presets';
import { SceneStore } from '../core/SceneStore';
import { SettingsStore } from '../core/SettingsStore';
import { decodeScene, shareUrl, sharedSceneParam } from '../core/share';
import { indexedKV, localKV } from '../core/storage';
import {
  keepPinned,
  oscillation,
  pickNext,
  rng,
  surpriseScene,
  vizPool,
  type Pins,
} from '../core/variety';
import {
  Engine,
  type AudioDevices,
  type GridStatus,
  type SourceInfo,
  type SourceKind,
} from '../engine/Engine';
import { PALETTES } from '../engine/render/palettes';
import { VISUALIZATIONS } from '../engine/render/visualizations';
import { TERMS_VERSION } from '../ui/terms';
import { detectCapabilities, type Capabilities } from '../platform/capabilities';
import type { BeatGrid, OutputConfig, RenderStats } from '../shared/messages';
import { OSC_SOURCE, type Modulation } from '../shared/modulation';
import { POSTFX } from '../shared/postfx';
import { idleMood, type MoodState } from '../shared/mood';
import {
  newScene,
  resolveTarget,
  sanitizeScene,
  uid,
  type Layer,
  type PresetSettings,
  type Scene,
  type SceneCommand,
  type TransitionSpec,
  type TransitionType,
  type Quantize,
} from '../shared/scene';
import type { Settings } from '../shared/settings';
import { idleTransport, type TransportState } from '../shared/transport';
import { vizRole, type VisualizationDef } from '../shared/viz';
import { browserFullscreen, keepAwake, toggleFullscreen } from '../ui/display';
import { Recorder, recordingFileName, recordingSupported } from './recorder';

export type Tab = 'scene' | 'visualizations' | 'presets' | 'audio' | 'outputs' | 'settings';

/** A scene on its way to the screens in preview mode. */
export interface TakeInfo {
  id: number;
  name: string;
  scene: Scene;
  type: TransitionType;
  /** Length of a full fade (also the rate when resuming part-way). */
  durationMs: number;
  /** Where the T-bar holds it (when not running on its own). */
  position: number;
  auto: { startMs: number; from: number } | null;
}

export interface Toast {
  id: number;
  message: string;
  kind: 'info' | 'error';
}

const SCENE_KEY = 'glowtrope.scene.v2';
const LEGACY_SCENE_KEY = 'glowtrope.scene.v1';
/**
 * v2: the zoned layout (left hand performs, right hand tweaks). Keymaps saved for the old layout
 * are left behind rather than mixed with it.
 */
const KEYMAP_KEY = 'glowtrope.keymap.v2';
/** Which on-stage panes are showing (per device). */
const PANES_KEY = 'glowtrope.panes.v1';
/** What randomizing leaves alone (per device; not part of presets). */
const PINS_KEY = 'glowtrope.pins.v1';
/** Visualizations hidden from the browser, and kept out of random picks (per device). */
const VIZ_PREFS_KEY = 'glowtrope.vizPrefs.v1';
/** Actions that existed when the saved keymap was written (see upgradeKeymap). */
const KEYMAP_ACTIONS_KEY = 'glowtrope.keymap.v2.actions';
const knownActions = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : ACTIONS.map((a) => a.id);
/** Set once the first-visit tour has been finished or skipped. */
const TOUR_KEY = 'glowtrope.tour.v1';
/** The terms version the visitor agreed to. */
const TERMS_KEY = 'glowtrope.terms';
/** The music chosen in the welcome last time, offered first next time. */
const WELCOME_SOURCE_KEY = 'glowtrope.welcome.source';
/** Leaving performance mode with Escape needs a second press within this window. */
const ESCAPE_CONFIRM_MS = 1500;
const MAX_RENDER_PIXELS = 3840 * 2160;

function lastWelcomeSource(): SourceKind | 'later' {
  const v = localKV.get(WELCOME_SOURCE_KEY);
  // Files can't be reopened without picking them again, so offer the demo instead.
  return v === 'mic' || v === 'tab' || v === 'later' ? v : 'demo';
}

export class AppController {
  readonly caps: Capabilities = detectCapabilities();

  // ---- reactive state
  scene = $state.raw<Scene>(newScene());
  settings = $state.raw<Settings>(null as unknown as Settings);
  stats = $state.raw<RenderStats | null>(null);
  transport = $state.raw<TransportState>(idleTransport());
  mood = $state.raw<MoodState>(idleMood());
  source = $state.raw<SourceInfo | null>(null);
  renderer = $state.raw<{ name: string; mode: 'worker' | 'main' } | null>(null);
  media = $state.raw<{ time: number; duration: number } | null>(null);
  grid = $state.raw<GridStatus | null>(null);
  toasts = $state<Toast[]>([]);
  /** A source is playing (the first one needs a click: browsers block sound until then). */
  started = $state(false);
  /** The renderer is running, with or without sound. */
  ready = $state(false);
  busy = $state(false);
  panelOpen = $state(true);
  hudVisible = $state(false);
  performance = $state(false);
  helpOpen = $state(false);
  /** The Live pane (bank, BPM, beat, tap) and the favorites/banks strip, shown independently. */
  panes = $state<{ live: boolean; strip: boolean }>(loadPanes());
  /** Pinned colours, effects and layers: Surprise me and Auto show keep them. */
  pins = $state<Pins>(loadPins());
  /** Visualizations the user hid, or excluded from Surprise me and Auto show. */
  vizPrefs = $state<{ hidden: string[]; noRandom: string[] }>(loadVizPrefs());
  /** Preview mode: the main window shows what you're preparing; outputs show live. */
  previewMode = $state(false);
  /** The live scene while in preview mode. */
  program = $state.raw<Scene | null>(null);
  /** Style and length of a "Fade" take, and the T-bar's style. */
  takeStyle = $state<{ type: Exclude<TransitionType, 'cut'>; seconds: number }>({
    type: 'crossfade',
    seconds: 1,
  });
  /** Takes fading in, oldest first (mirrors the renderer, for the T-bar and the notices). */
  takes = $state.raw<TakeInfo[]>([]);
  /** performance.now(), ticking while fades run. */
  clock = $state(performance.now());
  private takeId = 0;
  private takeClockRunning = false;
  /** Current step of the guided tour, or null when it isn't showing. */
  tourStep = $state<number | null>(null);
  /** The welcome dialog, shown each time the app opens (unless turned off in Settings). */
  welcomeOpen = $state(false);
  /** Agreed to the current terms: the welcome is the short "welcome back" version. */
  termsAccepted = $state(localKV.get(TERMS_KEY) === TERMS_VERSION);
  readonly tourSeen = !!localKV.get(TOUR_KEY);
  /** What the welcome offers first: the visitor's last choice. */
  readonly welcomeSource: SourceKind | 'later' = lastWelcomeSource();
  /** Which Settings accordion section is open (kept across tab switches). */
  settingsOpen = $state<string | null>(null);
  /** performance.now() when the current recording started, or null. */
  recordingSince = $state<number | null>(null);
  /** The encoder is finishing the file (can take a while on a busy machine). */
  savingRecording = $state(false);
  /** Last few error messages, kept after their toasts fade (debug snapshot). */
  recentErrors = $state<string[]>([]);
  private recorder: Recorder | null = null;
  private liveFrameSink: ((bitmap: ImageBitmap) => void) | null = null;
  private escapeArmedUntil = 0;
  tab = $state<Tab>('scene');
  bank = $state(0);
  currentPresetId = $state<string | null>(null);
  selectedLayerId = $state('l1');
  libraryVersion = $state(0);
  blackout = $state(false);
  freeze = $state(false);
  keymap = $state.raw<Keymap>(defaultKeymap());
  midiVersion = $state(0);
  outputsVersion = $state(0);
  playlist = $state.raw<File[]>([]);
  playlistIndex = $state(-1);
  pending = $state<string | null>(null);
  customViz = $state.raw<VisualizationDef[]>([]);
  devices = $state.raw<AudioDevices>({ inputs: [], outputs: [] });
  inputDeviceId = $state<string | null>(null);
  outputDeviceId = $state<string | null>(null);
  canUndo = $state(false);
  canRedo = $state(false);
  /** Auto show: what it last changed, and bars until its next change (null when off). */
  autoLast = $state<string | null>(null);
  autoNextBars = $state<number | null>(null);
  lastShareUrl: string | null = null;
  milkdropViz = $state.raw<VisualizationDef[]>([]);
  milkdropState = $state<'off' | 'loading' | 'ready' | 'failed'>('off');
  /** Verified MilkDrop pack code, re-sent to the renderer when it (re)starts. */
  milkdropCode: string | null = null;

  // ---- stores and services
  readonly sceneStore: SceneStore;
  readonly settingsStore: SettingsStore;
  readonly library: PresetLibrary;
  readonly midi: MidiController;
  readonly outputs: OutputManager;
  private readonly director = new Director();
  /** Visualization and palette ids Auto show used recently, to avoid quick repeats. */
  private recentAuto: string[] = [];
  private engine: Engine | null = null;
  private stage: HTMLElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private flashSaved: Scene | null = null;
  private recent: string[] = [];
  private toastId = 0;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly gridKV = indexedKV('glowtrope-grids', 'grids');

  constructor() {
    const reduced =
      typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.settingsStore = new SettingsStore(
      localKV,
      reduced ? { reducedMotion: true, smoothing: 0.8 } : {},
    );
    const stored = localKV.get(SCENE_KEY) ?? localKV.get(LEGACY_SCENE_KEY);
    const initial = sanitizeScene(stored, newScene());
    this.sceneStore = new SceneStore(initial);
    this.scene = initial;
    this.selectedLayerId = initial.layers[0]!.id;
    this.settings = this.settingsStore.resolve(initial);
    const savedKeymap = localKV.get(KEYMAP_KEY);
    this.keymap = savedKeymap
      ? upgradeKeymap(sanitizeKeymap(savedKeymap), knownActions(localKV.get(KEYMAP_ACTIONS_KEY)))
      : defaultKeymap();
    this.library = new PresetLibrary(indexedKV('glowtrope', 'library'), builtInPresets);
    this.midi = new MidiController(this.midiHost(), localKV);
    // A plain string, not reactive state, so the built-in URL is fine here.
    // eslint-disable-next-line svelte/prefer-svelte-reactivity
    const outputUrl = new URL('output.html', document.baseURI).href;
    this.outputs = new OutputManager(this.outputHost(), outputUrl);

    this.sceneStore.subscribe(({ scene, transition, history }) => {
      this.scene = scene;
      this.canUndo = this.sceneStore.canUndo;
      this.canRedo = this.sceneStore.canRedo;
      if (!scene.layers.some((l) => l.id === this.selectedLayerId))
        this.selectedLayerId = scene.layers[0]!.id;
      // In preview mode every change goes to the preview; live only changes on a take.
      if (this.previewMode) this.engine?.setPreview(scene);
      else this.engine?.setScene(scene, transition);
      this.applySettings();
      if (history || transition) this.midi.releaseTakeover();
      this.persistScene();
    });
    this.settingsStore.subscribe(() => this.applySettings());
    this.library.subscribe(() => {
      this.libraryVersion++;
      this.syncCustomViz();
    });
    void this.library.load();
    void this.loadSharedScene();
    if (this.settings.midiEnabled) void this.midi.enable().then(() => this.midiVersion++);
    queueMicrotask(() => {
      if (this.settings.milkdropPack) void this.loadMilkdrop();
    });
  }

  attach(stage: HTMLElement, canvas: HTMLCanvasElement): void {
    this.stage = stage;
    this.canvas = canvas;
  }

  // ---- engine

  private getEngine(): Engine {
    this.engine ??= new Engine(
      {
        canvas: () => this.canvas!,
        size: () => this.renderSize(),
      },
      {
        stats: (s) => {
          this.stats = s;
          if (this.pending && s.transition !== 'waiting') this.pending = null;
        },
        transport: (t, m) => this.onTransport(t, m),
        source: (s) => {
          this.source = s;
          this.checkSharingBar();
        },
        renderer: (r) => (this.renderer = r),
        error: (m) => this.toast(m, 'error'),
        media: (pos) => (this.media = pos),
        grid: (g) => (this.grid = g),
        ended: () => this.playlistAdvance(),
        milkdrop: (names) => this.onMilkdropPresets(names),
        liveFrame: (bitmap) => {
          if (this.liveFrameSink) this.liveFrameSink(bitmap);
          else bitmap.close();
        },
        slowLayer: (layerId, name) => {
          this.sceneStore.dispatch({ type: 'layer.set', layerId, patch: { enabled: false } });
          this.toast(
            `“${name}” was too heavy for this GPU and was switched off. Lower its quality settings or the resolution before re-enabling it.`,
            'error',
          );
        },
      },
      this.sceneStore.scene,
      this.settings,
      {
        get: (id) => this.gridKV.get<BeatGrid>(id),
        set: (id, grid) => this.gridKV.set(id, grid),
      },
    );
    return this.engine;
  }

  private renderSize(): { width: number; height: number; dpr: number } {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const virt = this.outputs.virtualSize();
    if (virt) {
      const scale = Math.min(1, Math.sqrt(MAX_RENDER_PIXELS / (virt.width * virt.height)));
      return { width: virt.width * scale, height: virt.height * scale, dpr: 1 };
    }
    return { width: this.stage?.clientWidth ?? 1280, height: this.stage?.clientHeight ?? 720, dpr };
  }

  resize(): void {
    this.engine?.resize();
    this.checkSharingBar();
  }

  /** Get the visuals going before any sound, so the app opens straight onto them. */
  async prepare(): Promise<void> {
    try {
      await this.getEngine().prepare();
      this.onRendererReady();
    } catch {
      // start() retries and reports the error from a click
    }
  }

  private onRendererReady(): void {
    this.ready = true;
    this.engine?.setCustomViz(this.customViz);
    if (this.milkdropCode) this.engineLoadMilkdrop(this.milkdropCode);
  }

  /** Start (or switch) the audio source. Must be called from a user gesture. */
  async start(kind: SourceKind, opts: { file?: File; deviceId?: string } = {}): Promise<void> {
    this.busy = true;
    try {
      const deviceId =
        opts.deviceId ?? (kind === 'mic' ? (this.inputDeviceId ?? undefined) : undefined);
      await this.getEngine().start(kind, {
        ...(opts.file ? { file: opts.file } : {}),
        ...(deviceId ? { deviceId } : {}),
      });
      this.started = true;
      this.media = null;
      void keepAwake(true);
      if (!this.ready) this.onRendererReady();
      void this.refreshDevices();
      if (this.outputDeviceId) void this.engine?.setOutputDevice(this.outputDeviceId);
    } catch {
      // reported through the engine's error event
    } finally {
      this.busy = false;
    }
  }

  async playFiles(files: File[]): Promise<void> {
    const audio = files.filter(
      (f) =>
        f.type.startsWith('audio/') || /\.(mp3|wav|ogg|oga|flac|m4a|aac|opus|webm)$/i.test(f.name),
    );
    if (audio.length === 0) {
      this.toast('No audio files found.', 'error');
      return;
    }
    audio.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    this.playlist = audio;
    this.playlistIndex = 0;
    await this.start('file', { file: audio[0]! });
  }

  async playlistGo(index: number): Promise<void> {
    const f = this.playlist[index];
    if (!f) return;
    this.playlistIndex = index;
    await this.start('file', { file: f });
  }

  private playlistAdvance(): void {
    if (this.playlistIndex >= 0 && this.playlistIndex < this.playlist.length - 1)
      void this.playlistGo(this.playlistIndex + 1);
  }

  togglePlay(): void {
    this.engine?.togglePlay();
  }

  seek(seconds: number): void {
    this.engine?.seek(seconds);
  }

  async refreshDevices(): Promise<void> {
    if (this.engine) this.devices = await this.engine.devices();
  }

  async setInputDevice(id: string | null): Promise<void> {
    this.inputDeviceId = id;
    if (this.source?.kind === 'mic') await this.start('mic', id ? { deviceId: id } : {});
  }

  async setOutputDevice(id: string | null): Promise<void> {
    this.outputDeviceId = id;
    const ok = await this.engine?.setOutputDevice(id ?? '');
    if (ok === false) this.toast('This browser cannot choose the output device.', 'error');
  }

  // ---- scene editing

  dispatch(cmd: SceneCommand, coalesce?: string): void {
    this.sceneStore.dispatch(cmd, coalesce ? { coalesce } : {});
    if (cmd.type !== 'scene.rename') this.noteManualChange();
  }

  undo(): void {
    this.sceneStore.undo();
  }

  redo(): void {
    this.sceneStore.redo();
  }

  get selectedLayer(): Layer {
    return this.scene.layers.find((l) => l.id === this.selectedLayerId) ?? this.scene.layers[0]!;
  }

  vizDef(id: string): VisualizationDef | undefined {
    return VISUALIZATIONS.find((v) => v.id === id) ?? this.customViz.find((v) => v.id === id);
  }

  get allViz(): VisualizationDef[] {
    return [...VISUALIZATIONS, ...this.customViz];
  }

  // ---- on-stage panes

  setPane(pane: 'live' | 'strip', on: boolean): void {
    this.panes = { ...this.panes, [pane]: on };
    localKV.set(PANES_KEY, this.panes);
  }

  // ---- pins: what randomizing leaves alone

  isLayerPinned(layerId: string): boolean {
    return this.pins.layers.includes(layerId);
  }

  /** Pin or unpin the overall colours, the overall effects, or a layer. */
  togglePin(what: 'palette' | 'effects' | { layerId: string }): void {
    const ids = this.scene.layers.map((l) => l.id);
    const layers = this.pins.layers.filter((id) => ids.includes(id)); // forget layers that are gone
    let next: Pins;
    let label: string;
    if (what === 'palette' || what === 'effects') {
      next = { ...this.pins, layers, [what]: !this.pins[what] };
      label = `${what === 'palette' ? 'Colours' : 'Effects'} ${next[what] ? 'pinned' : 'unpinned'}`;
    } else {
      const on = !layers.includes(what.layerId);
      next = {
        ...this.pins,
        layers: on ? [...layers, what.layerId] : layers.filter((id) => id !== what.layerId),
      };
      const layer = this.scene.layers.find((l) => l.id === what.layerId);
      label = `${(layer && this.vizDef(layer.vizId)?.name) ?? 'Layer'} ${on ? 'pinned' : 'unpinned'}`;
    }
    this.pins = next;
    localKV.set(PINS_KEY, next);
    this.toast(
      label.endsWith('unpinned')
        ? `${label}.`
        : `${label}: Surprise me and Auto show will keep it.`,
    );
  }

  // ---- managing visualizations

  isHidden(id: string): boolean {
    return this.vizPrefs.hidden.includes(id);
  }

  inRandom(id: string): boolean {
    return !this.vizPrefs.noRandom.includes(id) && !this.isHidden(id);
  }

  setVizHidden(id: string, hidden: boolean): void {
    this.updateVizPrefs({
      hidden: hidden
        ? [...this.vizPrefs.hidden.filter((x) => x !== id), id]
        : this.vizPrefs.hidden.filter((x) => x !== id),
    });
  }

  setVizRandom(id: string, include: boolean): void {
    this.updateVizPrefs({
      noRandom: include
        ? this.vizPrefs.noRandom.filter((x) => x !== id)
        : [...this.vizPrefs.noRandom.filter((x) => x !== id), id],
    });
  }

  private updateVizPrefs(patch: Partial<{ hidden: string[]; noRandom: string[] }>): void {
    this.vizPrefs = { ...this.vizPrefs, ...patch };
    localKV.set(VIZ_PREFS_KEY, this.vizPrefs);
  }

  /** Visualizations to offer in the scene's pickers (hidden ones left out). */
  get browsableViz(): VisualizationDef[] {
    return this.allViz.filter((v) => !this.isHidden(v.id));
  }

  /** Visualizations Surprise me and Auto show may pick. */
  private get randomViz(): VisualizationDef[] {
    return this.allViz.filter((v) => this.inRandom(v.id));
  }

  /** Remove an uploaded (ISF) visualization from this device's library. */
  async deleteCustomViz(id: string): Promise<void> {
    await this.library.removeVisualization(id);
    this.updateVizPrefs({
      hidden: this.vizPrefs.hidden.filter((x) => x !== id),
      noRandom: this.vizPrefs.noRandom.filter((x) => x !== id),
    });
  }

  // ---- per-layer looks: each can be "overall" or "this layer only"

  /** Give a layer its own palette (starting from the overall one), or back to the overall. */
  setLayerOwnPalette(layerId: string, own: boolean): void {
    this.dispatch({
      type: 'layer.palette',
      layerId,
      palette: own ? { ...this.scene.palette } : null,
    });
  }

  /**
   * Move an effect between the whole picture and one layer, keeping its settings: "This layer"
   * takes the overall effect onto the layer only; "All layers" puts the layer's back on top.
   */
  setEffectScope(layerId: string, fxId: string, scope: 'all' | 'layer'): void {
    const def = POSTFX.find((f) => f.id === fxId);
    const layer = this.scene.layers.find((l) => l.id === layerId);
    if (!def || !layer) return;
    const key = `scope:${layerId}:${fxId}:${Date.now()}`;
    if (scope === 'layer') {
      for (const p of def.params) {
        const value = this.scene.postfx[fxId]?.[p.id] ?? p.default;
        this.dispatch({ type: 'layer.fx.param', layerId, fxId, paramId: p.id, value }, key);
      }
      this.dispatch({ type: 'fx.reset', fxId }, key);
    } else {
      for (const p of def.params) {
        const value = layer.postfx?.[fxId]?.[p.id] ?? p.default;
        this.dispatch({ type: 'fx.param', fxId, paramId: p.id, value }, key);
      }
      this.dispatch({ type: 'layer.fx.reset', layerId, fxId }, key);
    }
  }

  addLayer(vizId: string): void {
    const id = uid('l');
    this.dispatch({
      type: 'layer.add',
      layer: {
        id,
        vizId,
        params: {},
        blend: this.scene.layers.length ? (this.vizDef(vizId)?.blend ?? 'add') : 'normal',
        opacity: this.vizDef(vizId)?.blend ? 1 : 0.8,
        enabled: true,
      },
    });
    this.selectedLayerId = id;
  }

  // ---- variety: surprise, oscillate

  /** "Surprise me": random visualizations (often two layers), palette and effects. */
  surprise(): void {
    const scene = keepPinned(
      this.scene,
      surpriseScene(
        this.scene,
        {
          backgrounds: vizPool(this.randomViz, 'background', this.settings.cycleMilkdrop),
          elements: vizPool(this.randomViz, 'element', false),
          palettes: PALETTES,
          // A pinned background leaves the layer on top as the surprise: always add one.
          ...(this.isLayerPinned(this.scene.layers[0]!.id) ? { elementChance: 1 } : {}),
        },
        rng(Date.now()),
      ),
      this.pins,
    );
    this.sceneStore.replace(scene, { ...scene.transition, quantize: 'immediate' });
    this.currentPresetId = null;
    const fresh = scene.layers.filter((l) => !this.isLayerPinned(l.id));
    this.selectedLayerId = (fresh.at(-1) ?? scene.layers.at(-1)!).id;
    this.noteManualChange();
  }

  /** The oscillation (own-oscillator modulation) driving a target, if any. */
  oscillationOf(target: string): Modulation | undefined {
    return this.scene.modulations.find((m) => m.target === target && m.source === OSC_SOURCE);
  }

  /** Swing a parameter around its value (or stop it). `beats` per cycle; `depth` 0..1 of range. */
  setOscillation(
    target: string,
    spec: { beats: number; depth: number; shape: NonNullable<Modulation['osc']>['shape'] } | null,
  ): void {
    const cur = this.oscillationOf(target);
    if (!spec) {
      if (cur) this.dispatch({ type: 'mod.remove', id: cur.id });
      return;
    }
    if (cur) {
      this.dispatch(
        {
          type: 'mod.update',
          id: cur.id,
          patch: {
            amount: spec.depth,
            osc: { shape: spec.shape, rate: spec.beats, sync: true },
          },
        },
        `osc:${target}`,
      );
    } else
      this.dispatch({
        type: 'mod.add',
        modulation: oscillation(target, spec.beats, spec.depth, spec.shape),
      });
  }

  cycleViz(delta: number): void {
    const list = this.allViz;
    const layer = this.selectedLayer;
    const i = list.findIndex((v) => v.id === layer.vizId);
    const next = list[(((i + delta) % list.length) + list.length) % list.length]!;
    this.dispatch({ type: 'layer.set', layerId: layer.id, patch: { vizId: next.id } });
  }

  cyclePalette(delta: number): void {
    const ids = [...PALETTES.map((p) => p.id)];
    const i = ids.indexOf(this.scene.palette.id);
    const id = ids[(((i + delta) % ids.length) + ids.length) % ids.length]!;
    this.dispatch({ type: 'palette.set', palette: { id } });
  }

  // ---- settings

  private applySettings(): void {
    this.settings = this.settingsStore.resolve(this.sceneStore.scene);
    this.engine?.setSettings(this.settings);
  }

  setSetting<K extends keyof Settings>(key: K, value: Settings[K]): void {
    const preset = this.settingsStore.set(key, value);
    if (preset)
      this.dispatch(
        { type: 'setting.set', key: preset.presetKey, value: preset.value },
        `setting:${key}`,
      );
    if (key === 'milkdropPack' && value) void this.loadMilkdrop();
    if (key === 'midiEnabled') {
      if (value)
        void this.midi.enable().then((ok) => {
          if (!ok) this.toast('MIDI is not available (this browser or permission).', 'error');
          this.midiVersion++;
        });
      else this.midi.disable();
    }
  }

  /** Save a preset-scope value as the default for scenes that don't set it. */
  setPresetSettingDefault(key: keyof PresetSettings): void {
    const v = this.scene.settings[key];
    if (v !== undefined) this.settingsStore.setUserDefault(key, v);
    this.dispatch({ type: 'setting.set', key, value: undefined });
  }

  // ---- presets

  presetsInBank(bank = this.bank): (Preset | null)[] {
    void this.libraryVersion;
    return this.library.bank(bank);
  }

  allPresets(): Preset[] {
    void this.libraryVersion;
    return this.library.list();
  }

  /** Load a preset with its transition (optionally overriding quantization). */
  applyPreset(
    p: Preset,
    opts: { quantize?: Quantize; immediate?: boolean; manual?: boolean; keepPins?: boolean } = {},
  ): void {
    const original = structuredClone(p.scene) as Scene;
    // A random pick (not one you chose) keeps what's pinned.
    const scene = opts.keepPins ? keepPinned(this.scene, original, this.pins) : original;
    let transition: TransitionSpec = { ...scene.transition };
    if (opts.quantize) transition.quantize = opts.quantize;
    if (opts.immediate)
      transition = { type: 'cut', duration: 0, unit: 'ms', quantize: 'immediate' };
    this.sceneStore.replace(scene, transition);
    // With pinned parts mixed in, it's no longer quite that preset (so Save won't overwrite it).
    this.currentPresetId = scene === original ? p.id : null;
    this.selectedLayerId = scene.layers[0]!.id;
    this.recent = [p.id, ...this.recent.filter((r) => r !== p.id)].slice(0, 6);
    if (scene === original) this.scheduleThumbnail(p.id);
    if (transition.quantize !== 'immediate' && this.transport.locked)
      this.pending = `${p.name} · on next ${transition.quantize}`;
    if (opts.manual !== false) this.noteManualChange();
  }

  /** Presets without a picture get one once they've been on screen for a moment. */
  private scheduleThumbnail(id: string): void {
    if (this.library.get(id)?.thumbnail) return;
    setTimeout(async () => {
      if (this.currentPresetId !== id || this.stats?.transition !== 'none') return;
      const thumb = await this.captureThumbnail();
      if (thumb && this.currentPresetId === id) await this.library.setThumbnail(id, thumb);
    }, 3000);
  }

  recall(bank: number, slot: number, quantize?: Quantize): void {
    const p = this.library.presetAt(bank, slot);
    if (!p) {
      this.toast(
        `Slot ${slot === 9 ? 0 : slot + 1} in ${this.library.bankName(bank)} is empty. Shift+${slot === 9 ? 0 : slot + 1} saves the current scene there.`,
      );
      return;
    }
    this.applyPreset(p, quantize ? { quantize } : {});
  }

  // Saving takes the scene the moment you press the button: the thumbnail is captured
  // asynchronously, and anything changed meanwhile must not end up in the saved preset.

  async store(bank: number, slot: number): Promise<void> {
    const scene = this.sceneStore.scene;
    const thumb = await this.captureThumbnail();
    const p = await this.library.storeToSlot(bank, slot, scene, thumb ?? undefined);
    this.currentPresetId = p.id;
    this.toast(`Saved “${p.name}” to slot ${slot === 9 ? 0 : slot + 1}.`);
  }

  async saveAs(name: string): Promise<void> {
    const scene = this.sceneStore.scene;
    const thumb = await this.captureThumbnail();
    const p = await this.library.save({
      name,
      scene,
      ...(thumb ? { thumbnail: thumb } : {}),
    });
    this.currentPresetId = p.id;
    this.dispatch({ type: 'scene.rename', name: p.name });
    this.toast(`Saved preset “${p.name}”.`);
  }

  async overwriteCurrent(): Promise<void> {
    const cur = this.currentPresetId ? this.library.get(this.currentPresetId) : undefined;
    if (!cur || cur.builtIn)
      return this.saveAs(this.scene.name === 'Untitled' ? 'My preset' : this.scene.name);
    const scene = this.sceneStore.scene;
    const thumb = await this.captureThumbnail();
    await this.library.save({
      id: cur.id,
      name: cur.name,
      scene,
      ...(thumb ? { thumbnail: thumb } : {}),
    });
    this.toast(`Updated “${cur.name}”.`);
  }

  flashStart(slot: number): void {
    const p = this.library.presetAt(this.bank, slot);
    if (!p || this.flashSaved) return;
    this.flashSaved = this.sceneStore.scene;
    this.applyPreset(p, { immediate: true, manual: false });
  }

  flashEnd(): void {
    if (!this.flashSaved) return;
    this.sceneStore.replace(this.flashSaved, {
      type: 'cut',
      duration: 0,
      unit: 'ms',
      quantize: 'immediate',
    });
    this.flashSaved = null;
  }

  stepPreset(delta: number): void {
    const list = this.library.bankPresets(this.bank);
    if (list.length === 0) {
      this.cycleViz(delta);
      return;
    }
    const i = list.findIndex((p) => p.id === this.currentPresetId);
    const next =
      list[
        ((((i < 0 ? (delta > 0 ? -1 : 0) : i) + delta) % list.length) + list.length) % list.length
      ]!;
    this.applyPreset(next);
  }

  randomPreset(): void {
    const pool = this.allPresets().map((p) => ({ id: p.id, tags: p.scene.tags }));
    const id = choosePreset(
      pool,
      this.currentPresetId,
      'shuffle',
      this.mood,
      this.recent,
      Date.now(),
    );
    const p = id ? this.library.get(id) : undefined;
    if (p) this.applyPreset(p, { keepPins: true });
  }

  // ---- favorites (unlimited, independent of banks)

  favorites(): Preset[] {
    void this.libraryVersion;
    return this.library.favorites();
  }

  /** True when what's on screen is an unchanged favorite. */
  get currentIsFavorite(): boolean {
    void this.libraryVersion;
    const p = this.currentPresetId ? this.library.get(this.currentPresetId) : undefined;
    return !!p && this.library.isFavorite(p.id) && sameLook(p.scene, this.scene);
  }

  /**
   * Favorite what's on screen. An unchanged preset is simply marked (or unmarked) as a
   * favorite; anything else is saved as a new favorite with a descriptive name.
   */
  async toggleFavorite(): Promise<void> {
    await this.setFavorite(!this.currentIsFavorite);
  }

  /**
   * Favorite (on) or unfavorite (off) what's on screen. Idempotent, so the keys can be pressed
   * without checking first: favoriting a favorite or unfavoriting anything else changes nothing.
   */
  async setFavorite(on: boolean): Promise<void> {
    const p = this.currentPresetId ? this.library.get(this.currentPresetId) : undefined;
    const unchanged = !!p && sameLook(p.scene, this.scene);
    if (!on) {
      if (p && unchanged && this.library.isFavorite(p.id)) {
        await this.library.setFavorite(p.id, false);
        this.toast(`Removed “${p.name}” from favorites.`);
      } else this.toast('What’s on screen isn’t a favorite.');
      return;
    }
    if (p && unchanged) {
      if (this.library.isFavorite(p.id)) this.toast(`“${p.name}” is already a favorite.`);
      else {
        await this.library.setFavorite(p.id, true);
        this.toast(`Added “${p.name}” to favorites.`);
      }
      return;
    }
    const scene = this.scene;
    const name = this.describeScene(scene);
    const thumb = await this.captureThumbnail();
    const fav = await this.library.addFavorite({ ...scene, name }, name, thumb ?? undefined);
    this.currentPresetId = fav.id;
    this.toast(`Added “${fav.name}” to favorites.`);
  }

  /** Step through favorites (wraps). */
  stepFavorite(delta: number): void {
    const list = this.library.favorites();
    if (list.length === 0) {
      this.toast(
        `No favorites yet. Press Favorite${keyHint(this.keymap, 'favorite.add').replace(' (', ' (or ')} to add what’s on screen.`,
      );
      return;
    }
    const i = list.findIndex((p) => p.id === this.currentPresetId);
    const next =
      list[
        ((((i < 0 ? (delta > 0 ? -1 : 0) : i) + delta) % list.length) + list.length) % list.length
      ]!;
    this.applyPreset(next);
  }

  /** "Kali Zoom + Beat Rings · Ocean": a readable name from the layers and palette. */
  describeScene(scene = this.scene): string {
    const layers = scene.layers
      .filter((l) => l.enabled)
      .map((l) => this.vizDef(l.vizId)?.name ?? l.vizId);
    const palette =
      scene.palette.name ?? PALETTES.find((p) => p.id === scene.palette.id)?.name ?? '';
    return `${layers.join(' + ')}${palette ? ` · ${palette}` : ''}`.slice(0, 80);
  }

  setBank(bank: number): void {
    this.bank = ((bank % BANKS) + BANKS) % BANKS;
  }

  private async captureThumbnail(): Promise<string | null> {
    if (!this.engine) return null;
    try {
      const img = await this.engine.thumbnail(192, 108);
      const c = document.createElement('canvas');
      c.width = img.width;
      c.height = img.height;
      const ctx = c.getContext('2d')!;
      const data = ctx.createImageData(img.width, img.height);
      // readPixels is bottom-up; flip rows.
      for (let y = 0; y < img.height; y++) {
        const src = (img.height - 1 - y) * img.width * 4;
        data.data.set(img.pixels.subarray(src, src + img.width * 4), y * img.width * 4);
      }
      ctx.putImageData(data, 0, 0);
      return c.toDataURL('image/jpeg', 0.75);
    } catch {
      return null;
    }
  }

  // ---- sharing, import, export

  async share(): Promise<void> {
    const { url, tooLong } = await shareUrl(this.sceneStore.scene, location.href);
    this.lastShareUrl = url;
    if (tooLong) {
      this.toast(
        'This scene is too large for a link. Use “Export preset” to share a file instead.',
        'error',
      );
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      this.toast('Share link copied to the clipboard.');
    } catch {
      history.replaceState(null, '', url);
      this.toast('Share link is in the address bar.');
    }
  }

  private async loadSharedScene(): Promise<void> {
    const param = sharedSceneParam(location.hash);
    if (!param) return;
    const scene = await decodeScene(param);
    if (scene) {
      this.sceneStore.replace(scene);
      this.toast(`Loaded shared scene “${scene.name}”.`);
    } else {
      this.toast('That share link is damaged or from an incompatible version.', 'error');
    }
    history.replaceState(null, '', location.pathname + location.search);
  }

  download(name: string, data: unknown): void {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  exportPreset(id: string): void {
    const doc = this.library.exportPreset(id);
    if (doc) this.download(`${slug(this.library.get(id)!.name)}.glowpreset.json`, doc);
  }

  exportScene(): void {
    this.download(`${slug(this.scene.name)}.glowpreset.json`, {
      format: 'glowtrope-preset',
      version: 1,
      preset: { name: this.scene.name, scene: this.scene },
    });
  }

  exportBank(): void {
    this.download(
      `${slug(this.library.bankName(this.bank))}.glowbank.json`,
      this.library.exportBank(this.bank),
    );
  }

  exportLibrary(): void {
    this.download('library.glowlibrary.json', this.library.exportLibrary());
  }

  exportSettings(): void {
    this.download('settings.glowprofile.json', {
      format: 'glowtrope-profile',
      version: 1,
      ...this.settingsStore.exportProfile(),
      keymap: this.keymap,
    });
  }

  /** Import presets/banks/libraries (JSON), ISF shaders (.fs), or a settings profile. */
  async importFiles(files: File[]): Promise<void> {
    let presets = 0;
    let shaders = 0;
    for (const f of files) {
      try {
        const text = await f.text();
        if (/\.(fs|frag|isf|glsl)$/i.test(f.name) || text.trimStart().startsWith('/*')) {
          await this.importIsf(f.name, text);
          shaders++;
          continue;
        }
        const doc = JSON.parse(text) as Record<string, unknown>;
        if (doc.format === 'glowtrope-profile') {
          this.settingsStore.importProfile(doc);
          if (doc.keymap) this.setKeymap(sanitizeKeymap(doc.keymap));
          this.toast('Imported settings profile.');
          continue;
        }
        if (doc.format === 'butterchurn' || (doc.baseVals && doc.shapes)) {
          await this.importButterchurn(f.name, doc);
          shaders++;
          continue;
        }
        presets += await this.library.importDocument(doc, this.bank);
      } catch (err) {
        this.toast(
          `Could not import ${f.name}: ${err instanceof Error ? err.message : String(err)}`,
          'error',
        );
      }
    }
    if (presets) this.toast(`Imported ${presets} preset${presets === 1 ? '' : 's'}.`);
    if (shaders) this.toast(`Imported ${shaders} visualization${shaders === 1 ? '' : 's'}.`);
  }

  /** Hook for the ISF loader (installed by ../isf). */
  importIsf = async (_name: string, _source: string): Promise<void> => {
    throw new Error('ISF import is not available');
  };

  /** Hook for MilkDrop presets via Butterchurn (installed by ../butterchurn). */
  importButterchurn = async (_name: string, _preset: unknown): Promise<void> => {
    throw new Error('MilkDrop import is not available');
  };

  /** Rebuild custom visualization defs from the library (ISF/Butterchurn parse on demand). */
  customVizFromLibrary: (
    sources: { id: string; name: string; source: string }[],
  ) => VisualizationDef[] = () => [];

  syncCustomViz(): void {
    const defs = [
      ...this.customVizFromLibrary(this.library.listVisualizations()),
      ...this.milkdropViz,
    ];
    this.customViz = defs;
    this.engine?.setCustomViz(defs);
  }

  engineLoadMilkdrop(code: string): void {
    if (!this.engine) return;
    this.milkdropState = 'loading';
    this.engine.loadMilkdropPack(code);
  }

  /** Hook for the MilkDrop pack loader (installed by ../app/content). */
  loadMilkdrop: () => Promise<void> = async () => undefined;

  private onMilkdropPresets(names: string[]): void {
    this.milkdropViz = names.map((n) => ({
      id: `md:${n}`,
      name: n,
      description: 'MilkDrop preset (Butterchurn community pack)',
      params: [],
      fragment: '',
      kind: 'butterchurn' as const,
      source: n,
      tags: ['style:milkdrop'],
      credit:
        'MilkDrop preset from the Butterchurn community pack (MIT-licensed package; presets by their respective authors).',
    }));
    this.milkdropState = names.length ? 'ready' : 'failed';
    this.syncCustomViz();
  }

  // ---- beat controls

  tap(): void {
    this.engine?.tap();
  }

  // ---- master

  setBlackout(on: boolean): void {
    this.blackout = on;
    this.engine?.setMaster({ blackout: this.blackout, freeze: this.freeze });
  }

  setFreeze(on: boolean): void {
    this.freeze = on;
    this.engine?.setMaster({ blackout: this.blackout, freeze: this.freeze });
  }

  // ---- Auto show

  private onTransport(t: TransportState, m: MoodState): void {
    this.transport = t;
    this.mood = m;
    // Auto show changes what's live, so it pauses while you prepare a preview.
    if (this.previewMode) {
      this.autoNextBars = null;
      return;
    }
    const move = this.director.update(this.settings, t, m, performance.now() / 1000);
    if (move) this.makeMove(move, t);
    this.autoNextBars = this.director.barsToNext(this.settings, m);
  }

  /** Auto show on or off (C). */
  toggleAutoShow(): void {
    const on = !this.settings.autopilot;
    this.setSetting('autopilot', on);
    if (on) this.director.reset();
    else this.autoNextBars = null;
    this.toast(
      on ? 'Auto show on: it changes one thing at a time with the music.' : 'Auto show off.',
    );
  }

  /** V: change one thing now, the way Auto show would. */
  autoNext(): void {
    const move = this.director.next(this.settings, this.mood);
    if (move && this.makeMove(move, this.transport)) this.toast(this.autoLast!);
    else this.toast('Nothing to change: what Auto show may change is pinned or switched off.');
  }

  /** Carry out one Auto show move, landing on the beat, bar or phrase it asks for. */
  private makeMove(move: Move, t: TransportState): boolean {
    const s = this.settings;
    const ctx: MoveContext = {
      backgrounds: vizPool(this.randomViz, 'background', s.cycleMilkdrop),
      elements: vizPool(this.randomViz, 'element', false),
      palettes: PALETTES,
      pins: this.pins,
      energy: move.energy,
      recent: this.recentAuto,
      rand: Math.random,
    };
    if (move.kind === 'whole' && s.autopilotPool !== 'generate') {
      const pool = (
        s.autopilotPool === 'bank'
          ? this.library.bankPresets(this.bank)
          : s.autopilotPool === 'favorites'
            ? this.library.favorites()
            : this.library.list()
      ).map((p) => ({ id: p.id, tags: p.scene.tags }));
      const id = choosePreset(
        pool,
        this.currentPresetId,
        s.autopilotSelection,
        this.mood,
        this.recent,
        t.beatIndex,
      );
      const p = id ? this.library.get(id) : undefined;
      if (!p) {
        this.director.skip();
        return false;
      }
      this.applyPreset(p, { quantize: move.quantize, manual: false, keepPins: true });
      this.autoLast = `New look: ${p.name}`;
      this.director.done();
      return true;
    }
    const before = this.scene;
    const next =
      move.kind === 'colours'
        ? changeColours(before, ctx)
        : move.kind === 'background'
          ? changeBackground(before, ctx)
          : move.kind === 'top'
            ? changeTop(before, ctx)
            : move.kind === 'effects'
              ? changeEffects(before, ctx)
              : move.kind === 'calm'
                ? calmDown(before, ctx)
                : wholeNewLook(before, ctx);
    if (!next) {
      this.director.skip();
      return false;
    }
    this.sceneStore.replace(next, { ...next.transition, quantize: move.quantize });
    this.currentPresetId = null;
    if (!next.layers.some((l) => l.id === this.selectedLayerId))
      this.selectedLayerId = next.layers.at(-1)!.id;
    const used = [next.palette.id, ...next.layers.map((l) => l.vizId)].filter(
      (id) => id !== before.palette.id && !before.layers.some((l) => l.vizId === id),
    );
    this.recentAuto = [...used, ...this.recentAuto.filter((id) => !used.includes(id))].slice(0, 12);
    this.autoLast = this.describeMove(move, before, next);
    this.director.done();
    return true;
  }

  /** "New background: Nebula", for the Auto section and the Live pane. */
  private describeMove(move: Move, before: Scene, next: Scene): string {
    const name = (id: string | undefined) => (id ? (this.vizDef(id)?.name ?? id) : '');
    switch (move.kind) {
      case 'colours':
        return `Colours: ${PALETTES.find((p) => p.id === next.palette.id)?.name ?? next.palette.id}`;
      case 'background':
        return `Background: ${name(next.layers[0]?.vizId)}`;
      case 'top': {
        if (next.layers.length > before.layers.length)
          return `Added ${name(next.layers.at(-1)?.vizId)}`;
        if (next.layers.length < before.layers.length) return 'Took away a top layer';
        const changed = next.layers.find((l, i) => before.layers[i]?.vizId !== l.vizId);
        return `Top layer: ${name(changed?.vizId)}`;
      }
      case 'effects': {
        const fx = next.postfx;
        return fx.mirror
          ? fx.mirror.mode === 4
            ? 'Effects: kaleidoscope'
            : 'Effects: mirror'
          : fx.feedback
            ? 'Effects: trails'
            : 'Effects: cleared';
      }
      case 'calm':
        return 'Stripped back for the breakdown';
      default:
        return move.reason === 'drop' ? 'Whole new look for the drop' : 'Whole new look';
    }
  }

  private noteManualChange(): void {
    this.director.manual(this.settings);
  }

  private sharingBarHinted = false;

  /**
   * Capturing a tab, Chrome shows a "sharing this tab" bar that window full screen keeps and has no
   * button to hide. The page's own full screen does hide it, so point there (once per session).
   */
  private checkSharingBar(): void {
    if (this.sharingBarHinted || this.source?.kind !== 'tab' || !browserFullscreen()) return;
    this.sharingBarHinted = true;
    this.toast(
      `To hide the “sharing this tab” bar, use Glowtrope’s own full screen${this.keyHint('ui.fullscreen')} or the fullscreen button at the top of the panel; the browser’s full screen keeps it.`,
    );
  }

  // ---- keyboard and actions

  /** " (T)": the key for an action, for tooltips, following any rebinding. */
  keyHint(action: string): string {
    return keyHint(this.keymap, action);
  }

  /** Follow the music, or hold the current tempo and bar (Live pane's Auto/Hold). */
  setBeatDetection(on: boolean): void {
    this.setSetting('beatDetection', on);
    this.toast(
      on
        ? 'Beat detection on: following the music.'
        : `Tempo held at ${this.transport.locked ? `${this.transport.steadyBpm.toFixed(1)} BPM` : 'tap tempo'}: tap or nudge to change it.`,
    );
  }

  // ---- layers from the keyboard (the selected layer)

  private selectedIndex(): number {
    return Math.max(
      0,
      this.scene.layers.findIndex((l) => l.id === this.selectedLayerId),
    );
  }

  /** Select the layer above (+1) or below (−1) in the stack. */
  selectLayer(delta: number): void {
    const layers = this.scene.layers;
    const i = Math.min(layers.length - 1, Math.max(0, this.selectedIndex() + delta));
    this.selectedLayerId = layers[i]!.id;
    this.toast(`Layer ${i + 1} of ${layers.length}: ${this.vizDef(layers[i]!.vizId)?.name ?? ''}`);
  }

  private nudgeLayerOpacity(delta: number): void {
    const l = this.selectedLayer;
    const opacity = Math.round(Math.min(1, Math.max(0, l.opacity + delta)) * 100) / 100;
    this.dispatch({ type: 'layer.set', layerId: l.id, patch: { opacity } }, `opacity:${l.id}`);
  }

  private removeSelectedLayer(): void {
    if (this.scene.layers.length <= 1) {
      this.toast('That’s the only layer.');
      return;
    }
    const i = this.selectedIndex();
    const l = this.selectedLayer;
    this.dispatch({ type: 'layer.remove', layerId: l.id });
    const layers = this.scene.layers;
    this.selectedLayerId = layers[Math.min(i, layers.length - 1)]!.id;
    this.toast(`Removed ${this.vizDef(l.vizId)?.name ?? 'the layer'}. Undo brings it back.`);
  }

  /** A random visualization of the same kind (full-screen or shapes) for the selected layer. */
  private randomizeLayerViz(): void {
    const layer = this.selectedLayer;
    if (this.isLayerPinned(layer.id)) {
      this.toast(
        `${this.vizDef(layer.vizId)?.name ?? 'This layer'} is pinned. Unpin it to change it.`,
      );
      return;
    }
    const pool = vizPool(
      this.randomViz,
      vizRole(this.vizDef(layer.vizId)),
      this.settings.cycleMilkdrop,
    );
    const v = pickNext(pool, layer.vizId, 'shuffle', this.recentAuto, rng(Date.now()));
    if (v) this.dispatch({ type: 'layer.set', layerId: layer.id, patch: { vizId: v.id } });
  }

  private addRandomLayer(): void {
    const pool = vizPool(this.randomViz, 'element', false);
    const v = pool[Math.floor(Math.random() * pool.length)];
    if (v) this.addLayer(v.id);
  }

  setKeymap(km: Keymap): void {
    this.keymap = km;
    localKV.set(KEYMAP_KEY, km);
    localKV.set(
      KEYMAP_ACTIONS_KEY,
      ACTIONS.map((a) => a.id),
    );
  }

  bindKey(chord: string, binding: Binding | null): string | null {
    if (isReserved(chord)) return `${chord} is reserved by the browser or system.`;
    const km = { ...this.keymap };
    if (binding) km[chord] = binding;
    else delete km[chord];
    this.setKeymap(km);
    return null;
  }

  /** Handle a key event from the control window or an output window. Returns true if consumed. */
  handleKey(e: KeyboardEvent, type: 'down' | 'up'): boolean {
    // The tour handles its own keys (arrows, Enter, Esc); the app's hotkeys wait.
    if (this.tourStep !== null || this.welcomeOpen) return false;
    const target = e.target as HTMLElement | null;
    const chord = chordOf(e);
    // Preview mode: Space/Enter fade and Shift+Space/Shift+Enter cut from anywhere (a focused
    // button, slider or menu included), so you never have to click away first. Text fields
    // still get their keys.
    if (
      this.previewMode &&
      (chord === 'Space' ||
        chord === 'Enter' ||
        chord === 'Shift+Space' ||
        chord === 'Shift+Enter') &&
      !isTextField(target)
    ) {
      e.preventDefault();
      if (type === 'down' && !e.repeat) {
        if (chord.startsWith('Shift')) this.cut();
        else this.fade();
      }
      return true;
    }
    const typing =
      !!target &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable);
    if (typing && e.key !== 'Escape') return false;
    if (!chord) return false;
    // Enter and Space on a focused button press that button, as everywhere else.
    if (
      (chord === 'Enter' || chord === 'Space') &&
      target?.closest('button, a, [role="button"], [role="tab"], [role="radio"], summary')
    )
      return false;
    const binding = this.keymap[chord];
    if (!binding) return false;
    if (!this.ready && !['ui.escape'].includes(binding.action)) return false;
    const def = ACTIONS.find((a) => a.id === binding.action);
    if (type === 'up' && !def?.momentary) return false;
    if (type === 'down' && e.repeat && def?.momentary) {
      e.preventDefault();
      return true;
    }
    e.preventDefault();
    this.runAction(binding.action, binding.arg, type === 'down');
    return true;
  }

  runAction(action: string, arg: number | undefined, down = true): void {
    const slot = arg ?? 0;
    if (!down) {
      if (action === 'preset.flash') this.flashEnd();
      return;
    }
    switch (action) {
      case 'preset.recall':
        return this.recall(this.bank, slot);
      case 'preset.store':
        return void this.store(this.bank, slot);
      case 'preset.flash':
        return this.flashStart(slot);
      case 'preset.next':
        return this.stepPreset(1);
      case 'preset.prev':
        return this.stepPreset(-1);
      case 'preset.random':
        return this.randomPreset();
      case 'bank.next':
        return this.setBank(this.bank + 1);
      case 'bank.prev':
        return this.setBank(this.bank - 1);
      case 'favorite.toggle':
        return void this.toggleFavorite();
      case 'favorite.add':
        return void this.setFavorite(true);
      case 'favorite.remove':
        return void this.setFavorite(false);
      case 'viz.random':
        return this.randomizeLayerViz();
      case 'layer.selectUp':
        return this.selectLayer(1);
      case 'layer.selectDown':
        return this.selectLayer(-1);
      case 'layer.moveUp':
        return this.dispatch({ type: 'layer.move', layerId: this.selectedLayer.id, delta: 1 });
      case 'layer.moveDown':
        return this.dispatch({ type: 'layer.move', layerId: this.selectedLayer.id, delta: -1 });
      case 'layer.opacityUp':
        return this.nudgeLayerOpacity(0.1);
      case 'layer.opacityDown':
        return this.nudgeLayerOpacity(-0.1);
      case 'layer.toggle': {
        const l = this.selectedLayer;
        return this.dispatch({ type: 'layer.set', layerId: l.id, patch: { enabled: !l.enabled } });
      }
      case 'layer.add':
        return this.addRandomLayer();
      case 'layer.remove':
        return this.removeSelectedLayer();
      case 'transport.detect':
        return this.setBeatDetection(!this.settings.beatDetection);
      case 'favorite.next':
        return this.stepFavorite(1);
      case 'favorite.prev':
        return this.stepFavorite(-1);
      case 'scene.surprise':
        return this.surprise();
      case 'ui.livePane':
        return this.setPane('live', !this.panes.live);
      case 'ui.strip':
        return this.setPane('strip', !this.panes.strip);
      case 'preview.toggle':
        return this.setPreviewMode(!this.previewMode);
      case 'preview.fade':
        return this.takeLive('fade');
      case 'preview.cut':
        return this.takeLive('cut');
      case 'autopilot.toggle':
        return this.toggleAutoShow();
      case 'auto.next':
        return this.autoNext();
      case 'viz.next':
        return this.cycleViz(1);
      case 'viz.prev':
        return this.cycleViz(-1);
      case 'palette.next':
        return this.cyclePalette(1);
      case 'palette.prev':
        return this.cyclePalette(-1);
      case 'transport.tap':
        return this.tap();
      case 'transport.resync':
        return this.engine?.resync();
      case 'transport.nudgeBack':
        return this.engine?.nudge(-1);
      case 'transport.nudgeForward':
        return this.engine?.nudge(1);
      case 'transport.half':
        return this.engine?.scaleTempo(0.5);
      case 'transport.double':
        return this.engine?.scaleTempo(2);
      case 'master.blackout':
        return this.setBlackout(!this.blackout);
      case 'master.freeze':
        return this.setFreeze(!this.freeze);
      case 'source.playPause':
        return this.togglePlay();
      case 'ui.fullscreen':
        return void toggleFullscreen();
      case 'ui.toggleControls':
        this.panelOpen = !this.panelOpen;
        return;
      case 'ui.toggleHud':
        this.hudVisible = !this.hudVisible;
        return;
      case 'ui.performance':
        return this.setPerformance(!this.performance);
      case 'ui.help':
        this.helpOpen = !this.helpOpen;
        return;
      case 'ui.escape':
        if (this.helpOpen) {
          this.helpOpen = false;
          return;
        }
        if (!this.performance) return;
        // Accidental-exit protection (CTL-9): a single stray Escape mid-show does nothing.
        if (performance.now() < this.escapeArmedUntil) {
          this.escapeArmedUntil = 0;
          this.setPerformance(false);
        } else {
          this.escapeArmedUntil = performance.now() + ESCAPE_CONFIRM_MS;
          this.toast('Press Esc again to leave performance mode.');
        }
        return;
      case 'output.open':
        return void this.openOutput(null);
      case 'output.identify':
        return this.outputs.identify();
      case 'output.record':
        return void this.toggleRecording();
      case 'edit.undo':
        return this.undo();
      case 'edit.redo':
        return this.redo();
    }
  }

  setPerformance(on: boolean): void {
    this.performance = on;
    this.panelOpen = !on;
    // Performance mode brings the Live pane up; it can still be hidden without leaving the mode.
    if (on && !this.panes.live) this.setPane('live', true);
    const kb = (
      navigator as Navigator & {
        keyboard?: { lock?: (keys?: string[]) => Promise<void>; unlock?: () => void };
      }
    ).keyboard;
    if (on && document.fullscreenElement) void kb?.lock?.(['Escape', 'Tab']).catch(() => undefined);
    if (!on) kb?.unlock?.();
  }

  // ---- outputs

  async openOutput(screenId: string | null): Promise<void> {
    if (!this.started) {
      this.toast('Start a source first.', 'error');
      return;
    }
    const id = this.outputs.open(screenId);
    if (!id)
      this.toast(
        'The output window was blocked. Allow pop-ups for this site and try again.',
        'error',
      );
    await tick();
  }

  async detectScreens(): Promise<void> {
    const screens = await this.outputs.detectScreens();
    if (screens.length === 0)
      this.toast('Screen details are unavailable (needs Chrome/Edge and permission).', 'error');
    this.outputsVersion++;
  }

  setOutputMode(mode: OutputMode): void {
    this.outputs.setMode(mode);
  }

  private outputHost() {
    return {
      addOutput: (
        id: string,
        canvas: HTMLCanvasElement,
        w: number,
        h: number,
        config: OutputConfig,
      ) => this.engine?.addOutput(id, canvas, w, h, config),
      updateOutput: (
        id: string,
        patch: { width?: number; height?: number; config?: Partial<OutputConfig> },
      ) => this.engine?.updateOutput(id, patch),
      removeOutput: (id: string) => this.engine?.removeOutput(id),
      onKey: (e: KeyboardEvent, type: 'down' | 'up') => {
        // An output window takes itself full screen; the control window can't from its keys.
        if (this.keymap[chordOf(e) ?? '']?.action === 'ui.fullscreen') return;
        this.handleKey(e, type);
      },
      onChange: () => {
        this.outputsVersion++;
        this.engine?.resize();
      },
    };
  }

  // ---- MIDI

  private midiHost() {
    return {
      paramValue: (path: string) => {
        const info = this.resolveMidiPath(path);
        if (!info) return null;
        return (info.base - info.min) / (info.max - info.min || 1);
      },
      setParam: (path: string, v: number) => this.setNormalized(path, v, `midi:${path}`),
      runAction: (action: string, arg: number | undefined, down: boolean) =>
        this.runAction(action, arg, down),
      ccSource: (source: string, value: number) => this.engine?.midiValue(source, value),
      clockBeat: (perfMs: number, bpm: number) => {
        if (this.settings.followMidiClock) this.engine?.externalBeat(perfMs, bpm);
      },
      clockStop: () => this.engine?.externalStop(),
      onChange: () => this.midiVersion++,
    };
  }

  /** MIDI paths use layer positions ("layer#0/speed") so mappings survive preset changes. */
  private concretePath(path: string): string {
    const m = /^layer#(\d+)\/(.+)$/.exec(path);
    if (!m) return path;
    const layer = this.scene.layers[Number(m[1])];
    return layer ? `layer/${layer.id}/${m[2]}` : path;
  }

  private resolveMidiPath(path: string): { min: number; max: number; base: number } | null {
    const p = this.concretePath(path);
    if (p.startsWith('setting/')) {
      const key = p.slice(8) as keyof Settings;
      const v = this.settings[key];
      return typeof v === 'number'
        ? { min: key === 'smoothing' ? 0 : 0, max: key === 'smoothing' ? 1 : 2, base: v }
        : null;
    }
    return resolveTarget(this.scene, p, (id) => this.vizDef(id));
  }

  /** Set a parameter from a 0..1 value (MIDI, XY pads). */
  setNormalized(path: string, v: number, coalesce: string): void {
    const p = this.concretePath(path);
    const info = this.resolveMidiPath(path);
    if (!info) return;
    const value = info.min + v * (info.max - info.min);
    const parts = p.split('/');
    if (parts[0] === 'layer' && parts[2] === 'fx' && parts.length === 5) {
      this.dispatch(
        { type: 'layer.fx.param', layerId: parts[1]!, fxId: parts[3]!, paramId: parts[4]!, value },
        coalesce,
      );
    } else if (parts[0] === 'layer' && parts[2] === 'palette') {
      this.dispatch({ type: 'layer.paletteShift', layerId: parts[1]!, value }, coalesce);
    } else if (parts[0] === 'layer' && parts[2] === 'view' && parts.length === 4) {
      this.dispatch({ type: 'layer.view', layerId: parts[1]!, key: parts[3]!, value }, coalesce);
    } else if (parts[0] === 'layer') {
      if (parts[2] === 'opacity')
        this.dispatch(
          { type: 'layer.set', layerId: parts[1]!, patch: { opacity: value } },
          coalesce,
        );
      else
        this.dispatch(
          { type: 'layer.param', layerId: parts[1]!, paramId: parts[2]!, value },
          coalesce,
        );
    } else if (parts[0] === 'fx') {
      this.dispatch({ type: 'fx.param', fxId: parts[1]!, paramId: parts[2]!, value }, coalesce);
    } else if (p === 'palette/shift') {
      this.dispatch({ type: 'palette.shift', value }, coalesce);
    } else if (p === 'master/intensity' || p === 'setting/intensity') {
      this.setSetting('intensity', value);
    } else if (parts[0] === 'setting') {
      this.setSetting(parts[1] as keyof Settings, value as never);
    }
  }

  midiLearn(target: MidiTarget | null): void {
    this.midi.learn(target);
  }

  // ---- toasts and persistence

  toast(message: string, kind: Toast['kind'] = 'info'): void {
    const id = ++this.toastId;
    if (kind === 'error') this.recentErrors = [...this.recentErrors.slice(-4), message];
    this.toasts = [...this.toasts.slice(-3), { id, message, kind }];
    setTimeout(
      () => (this.toasts = this.toasts.filter((t) => t.id !== id)),
      kind === 'error' ? 8000 : 3500,
    );
  }

  dismissToast(id: number): void {
    this.toasts = this.toasts.filter((t) => t.id !== id);
  }

  private persistScene(): void {
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => localKV.set(SCENE_KEY, this.sceneStore.scene), 300);
  }

  // ---- preview / live

  /**
   * Turn preview mode on or off. Turning it off goes back to editing what's live; anything
   * prepared but not taken stays reachable with undo.
   */
  setPreviewMode(on: boolean): void {
    if (on === this.previewMode) return;
    if (on) {
      if (!this.engine || !this.started) {
        this.toast('Start a source first.', 'error');
        return;
      }
      this.program = this.sceneStore.scene;
      this.takes = [];
      this.previewMode = true;
      this.engine.setPreview(this.sceneStore.scene);
      this.toast(
        'Preview mode: changes show here first. Take them live with Cut, Fade or the T-bar.',
      );
      return;
    }
    // The newest take (if any) goes live as preview mode ends; the renderer does the same.
    const live = this.takes.at(-1)?.scene ?? this.program;
    this.previewMode = false;
    this.program = null;
    this.takes = [];
    this.engine?.setPreview(null);
    if (live && !sameLook(live, this.sceneStore.scene)) this.sceneStore.replace(live);
  }

  /** Where a take is (0..1): its timer's progress, or where the T-bar holds it. */
  takePosition(t: TakeInfo, now = this.clock): number {
    return t.auto ? Math.min(1, t.auto.from + (now - t.auto.startMs) / t.durationMs) : t.position;
  }

  /** The T-bar shows (and controls) the newest take. */
  get tbarValue(): number {
    const t = this.takes.at(-1);
    return t ? this.takePosition(t) : 0;
  }

  /**
   * Fade (Space or Enter): resume the newest take from wherever the T-bar left it, or start a
   * new one. New fades stack on running ones and layer in at the same time.
   */
  fade(): void {
    if (!this.previewMode || !this.engine) return;
    const durationMs = this.takeStyle.seconds * 1000;
    const newest = this.takes.at(-1);
    if (newest && !newest.auto) {
      const from = newest.position;
      this.updateTake(newest.id, { auto: { startMs: performance.now(), from } });
      this.engine.take({ type: 'take.resume', id: newest.id, durationMs });
    } else {
      const id = ++this.takeId;
      this.engine.take({
        type: 'take.start',
        id,
        transition: this.takeStyle.type,
        durationMs,
        manual: false,
        position: 0,
      });
      this.takes = [
        ...this.takes,
        {
          id,
          name: this.describeScene(),
          scene: this.sceneStore.scene,
          type: this.takeStyle.type,
          durationMs,
          position: 0,
          auto: { startMs: performance.now(), from: 0 },
        },
      ];
    }
    this.runTakeClock();
  }

  /** Cut (Shift+Space or Shift+Enter): the preview goes live now, over everything fading. */
  cut(): void {
    if (!this.previewMode || !this.engine) return;
    this.engine.take({ type: 'take.cut' });
    this.program = this.sceneStore.scene;
    this.takes = [];
  }

  /** Back-compat for actions and buttons. */
  takeLive(kind: 'cut' | 'fade'): void {
    if (kind === 'cut') this.cut();
    else this.fade();
  }

  /** The T-bar is grabbed: a running fade stops where it is, under your hand. */
  tbarGrab(): void {
    const t = this.takes.at(-1);
    if (!t || !t.auto) return;
    const position = this.takePosition(t, performance.now());
    this.updateTake(t.id, { auto: null, position });
    this.engine?.take({ type: 'take.set', id: t.id, position });
  }

  /** The T-bar moves: steer the newest take, or start one (locking the preview). */
  tbarMove(position: number): void {
    if (!this.previewMode || !this.engine) return;
    const p = Math.max(0, Math.min(1, position));
    const t = this.takes.at(-1);
    if (t) {
      this.updateTake(t.id, { auto: null, position: p });
      this.engine.take({ type: 'take.set', id: t.id, position: p });
    } else if (p > 0) {
      const id = ++this.takeId;
      const durationMs = this.takeStyle.seconds * 1000;
      this.engine.take({
        type: 'take.start',
        id,
        transition: this.takeStyle.type,
        durationMs,
        manual: true,
        position: p,
      });
      this.takes = [
        ...this.takes,
        {
          id,
          name: this.describeScene(),
          scene: this.sceneStore.scene,
          type: this.takeStyle.type,
          durationMs,
          position: p,
          auto: null,
        },
      ];
    }
  }

  /**
   * The T-bar is let go (or the pointer leaves it). At the top, the take is complete and the bar
   * returns to 0; at the bottom, the take is dropped; in between, it stays held mid-stream.
   */
  tbarRelease(): void {
    const t = this.takes.at(-1);
    if (!t || t.auto) return;
    if (t.position >= 0.999) this.completeTake(t.id, true);
    else if (t.position <= 0.001) {
      this.engine?.take({ type: 'take.cancel', id: t.id });
      this.takes = this.takes.filter((x) => x.id !== t.id);
    }
  }

  private completeTake(id: number, tell: boolean): void {
    const i = this.takes.findIndex((x) => x.id === id);
    if (i < 0) return;
    if (tell) this.engine?.take({ type: 'take.complete', id });
    this.program = this.takes[i]!.scene;
    this.takes = this.takes.slice(i + 1);
  }

  private updateTake(id: number, patch: Partial<TakeInfo>): void {
    this.takes = this.takes.map((t) => (t.id === id ? { ...t, ...patch } : t));
  }

  /**
   * While fades run: tick the clock the progress displays use, and mark timed takes complete
   * when they finish (the renderer completes them on its own, on the same timing).
   */
  private runTakeClock(): void {
    if (this.takeClockRunning) return;
    this.takeClockRunning = true;
    const tick = () => {
      this.clock = performance.now();
      for (let i = this.takes.length - 1; i >= 0; i--) {
        const t = this.takes[i]!;
        if (t.auto && this.takePosition(t) >= 1) {
          this.completeTake(t.id, false);
          break;
        }
      }
      if (this.takes.some((t) => t.auto)) requestAnimationFrame(tick);
      else this.takeClockRunning = false;
    };
    requestAnimationFrame(tick);
  }

  // ---- welcome and guided tour

  /**
   * Show the welcome as the app opens. Always on a first visit or when the terms change; after
   * that unless the visitor turned it off.
   */
  maybeWelcome(): void {
    if (!this.termsAccepted || this.settings.showWelcome) this.welcomeOpen = true;
  }

  /** Esc or a click outside, once the terms are agreed: carry on without music for now. */
  dismissWelcome(): void {
    if (this.termsAccepted) this.welcomeOpen = false;
  }

  /**
   * "Agree and start" in the welcome dialog: record the agreement, start the chosen music (the
   * click is the gesture browsers need for sound and permissions) and maybe the tour.
   */
  async acceptWelcome(opts: {
    source: SourceKind | null;
    files: File[];
    tour: boolean;
  }): Promise<void> {
    localKV.set(TERMS_KEY, TERMS_VERSION);
    localKV.set(WELCOME_SOURCE_KEY, opts.source ?? 'later');
    this.termsAccepted = true;
    this.welcomeOpen = false;
    const started =
      opts.source === 'file'
        ? this.playFiles(opts.files)
        : opts.source
          ? this.start(opts.source)
          : Promise.resolve();
    if (opts.tour) this.startTour();
    else localKV.set(TOUR_KEY, true);
    await started;
  }

  startTour(): void {
    this.helpOpen = false;
    if (this.performance) this.setPerformance(false);
    this.tourStep = 0;
  }

  /** Finished or skipped: don't show it again automatically. */
  endTour(): void {
    this.tourStep = null;
    localKV.set(TOUR_KEY, true);
  }

  // ---- recording (OUT-7)

  readonly canRecord = recordingSupported();

  /**
   * The canvas a recording captures: the renderer sends it each live frame, so a recording is
   * exactly what's live (never the preview or its corner monitor), at up to 1080p.
   */
  private recordingCanvas(): HTMLCanvasElement {
    const { width, height, dpr } = this.renderSize();
    const scale = Math.min(dpr, 1920 / width, 1080 / height);
    const w = Math.max(2, Math.round((width * scale) / 2) * 2);
    const h = Math.max(2, Math.round((height * scale) / 2) * 2);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { alpha: false })!;
    this.liveFrameSink = (bitmap) => {
      ctx.drawImage(bitmap, 0, 0, w, h);
      bitmap.close();
    };
    this.engine!.recordLive(w, h);
    return canvas;
  }

  private stopLiveFrames(): void {
    this.engine?.recordLive(0, 0);
    this.liveFrameSink = null;
  }

  async toggleRecording(): Promise<void> {
    if (this.savingRecording) return;
    if (this.recorder) {
      const rec = this.recorder;
      this.recorder = null;
      this.recordingSince = null;
      this.savingRecording = true;
      const blob = await rec.stop().finally(() => (this.savingRecording = false));
      this.engine?.stopRecordingAudio();
      this.stopLiveFrames();
      if (blob.size === 0) {
        this.toast('The recording is empty.', 'error');
        return;
      }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = recordingFileName(rec.mimeType);
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
      this.toast(`Saved ${a.download} (${(blob.size / 1e6).toFixed(1)} MB).`);
      return;
    }
    if (!this.started || !this.canvas) {
      this.toast('Start a source first.', 'error');
      return;
    }
    if (!this.canRecord) {
      this.toast('This browser can’t record video.', 'error');
      return;
    }
    try {
      const rec = new Recorder();
      const fps = this.settings.targetFps === 'auto' ? 60 : this.settings.targetFps;
      rec.start(this.recordingCanvas(), this.engine?.recordingAudio() ?? null, fps);
      this.recorder = rec;
      this.recordingSince = performance.now();
    } catch (err) {
      this.engine?.stopRecordingAudio();
      this.stopLiveFrames();
      this.toast(
        `Couldn’t start recording: ${err instanceof Error ? err.message : String(err)}`,
        'error',
      );
    }
  }

  dispose(): void {
    if (this.recorder) void this.recorder.stop();
    this.outputs.closeAll();
    this.engine?.dispose();
  }

  /** Read-only snapshot for tests and debugging (window.__glow). */
  debugSnapshot(): unknown {
    return {
      stats: this.stats,
      transport: this.transport,
      mood: this.mood,
      source: this.source,
      renderer: this.renderer,
      errors: this.recentErrors,
      previewMode: this.previewMode,
      program: this.program,
      scene: this.scene,
      settings: this.settings,
      bank: this.bank,
      currentPresetId: this.currentPresetId,
      outputs: this.outputs.list(),
      grid: this.grid,
      blackout: this.blackout,
      freeze: this.freeze,
    };
  }
}

function slug(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'preset'
  );
}

export const SLOT_COUNT = SLOTS;

/** Same picture, ignoring the name (so a renamed scene still counts as the saved one). */
function sameLook(a: Scene, b: Scene): boolean {
  return JSON.stringify({ ...a, name: '' }) === JSON.stringify({ ...b, name: '' });
}

function loadVizPrefs(): { hidden: string[]; noRandom: string[] } {
  const raw = localKV.get(VIZ_PREFS_KEY) as { hidden?: unknown; noRandom?: unknown } | undefined;
  const ids = (v: unknown) =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, 2000) : [];
  return { hidden: ids(raw?.hidden), noRandom: ids(raw?.noRandom) };
}

function loadPins(): Pins {
  const raw = localKV.get(PINS_KEY) as Partial<Pins> | undefined;
  return {
    palette: raw?.palette === true,
    effects: raw?.effects === true,
    layers: Array.isArray(raw?.layers)
      ? raw.layers.filter((x): x is string => typeof x === 'string').slice(0, 16)
      : [],
  };
}

function loadPanes(): { live: boolean; strip: boolean } {
  const raw = localKV.get(PANES_KEY) as { live?: unknown; strip?: unknown } | undefined;
  return { live: raw?.live === true, strip: raw?.strip === true };
}

/** Somewhere typing goes: Space and Enter belong to it. */
function isTextField(el: HTMLElement | null): boolean {
  if (!el) return false;
  if (el.isContentEditable || el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  const t = (el as HTMLInputElement).type;
  return !['range', 'checkbox', 'radio', 'button', 'submit', 'color', 'file'].includes(t);
}
